import clsx from 'clsx'

import type { GlobalAccountCurrency } from '@/pages/NewGlobalAccount'

type CountrySelectionProps = {
  currencies: GlobalAccountCurrency[]
  selectedCountry: string
  onSelect: (country: GlobalAccountCurrency) => void
}

const CountrySelection = ({ selectedCountry, onSelect, currencies }: CountrySelectionProps) => {
  return (
    <div className="w-full mx-auto">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {currencies.map((country: GlobalAccountCurrency) => {
          const isSelected = country.name === selectedCountry

          return (
            <button
              key={country.name}
              onClick={() => onSelect(country)}
              aria-label={country.name}
              aria-selected={isSelected}
              className={clsx(
                'cursor-pointer relative flex flex-col items-center justify-center aspect-square p-2 rounded-xl border-2 transition-all duration-200 hover:shadow-lg',
                'bg-gray-50 dark:bg-gray-800',
                isSelected
                  ? 'border-blue-500 shadow-lg'
                  : ' hover:shadow-md border-gray-200 dark:border-gray-600 hover:border-gray-400',
              )}
            >
              {/* Selection indicator dot */}
              {isSelected && (
                <div
                  data-testid="selected-dot"
                  className="absolute top-2 right-2 size-2 rounded-full bg-blue-500"
                />
              )}

              {/* Flag */}
              <div className="text-3xl mb-3">{country.flag}</div>

              {/* Country name */}
              <div
                className={clsx(
                  'text-sm font-semibold text-center mb-3 leading-tight',
                  isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white',
                )}
              >
                {country.name}
              </div>

              {/* Currency code pill */}
              <div
                className={clsx(
                  'px-3 py-1.5 rounded-full text-xs font-semibold transition-all',
                  isSelected
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
                )}
              >
                {country.code}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default CountrySelection
